// eslint-disable-next-line @altermaind/you-shall-not-pass
import { FC, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Loader, Svg, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { useDebounce } from '@/_shared/hooks/useDebounce';
// eslint-disable-next-line @altermaind/no-build-import
import 'react-pdf/dist/Page/AnnotationLayer.css';
// eslint-disable-next-line @altermaind/no-build-import
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

type Props = {
  url: string;
  title?: string;
  startFrom?: number;
  onCloseDocument: () => void;
  onFinishReading?: () => void;
  onProgress?: (currentPage: number) => void;
};

const MAX_WIDTH = 900;
const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.75; // 75%
const MAX_ZOOM = 1.5; // 150%

export const Player: FC<Props> = ({
  url,
  title,
  startFrom,
  onCloseDocument,
  onFinishReading,
  onProgress,
}) => {
  const t = useTranslations('documents.player');
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(startFrom || 1);

  const debouncedOnProgress = useDebounce((page: number) => onProgress?.(page), 500);
  const [containerWidth, setContainerWidth] = useState<number>(MAX_WIDTH);
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [footerHeight, setFooterHeight] = useState(0);

  const onDocumentLoadSuccess = useCallback(({ numPages: total }: { numPages: number }) => {
    setNumPages(total);
    pageRefs.current = Array.from({ length: total }, () => null);
  }, []);

  const zoomIn = () => setZoom(prev => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  const zoomOut = () => setZoom(prev => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  const resetZoom = () => setZoom(1);

  const baseWidth = Math.min(containerWidth, MAX_WIDTH);
  const pageWidth = baseWidth * zoom;

  const pages = useMemo(() => Array.from({ length: numPages }, (_item, i) => i + 1), [numPages]);

  // Call onProgress when current page changes (debounced)
  useEffect(() => {
    if (currentPage > 0) {
      debouncedOnProgress?.(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Scroll to startFrom page when document loads
  useEffect(() => {
    if (startFrom && startFrom > 1 && numPages > 0) {
      // Delay to allow pages to render
      const timeoutId = setTimeout(() => {
        const pageRef = pageRefs.current[startFrom - 1];
        if (pageRef) {
          pageRef.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [startFrom, numPages]);

  // Track current page based on scroll position
  useEffect(() => {
    const container = containerRef.current;
    if (!container || numPages === 0) return undefined;

    const handleScroll = () => {
      const containerRect = container.getBoundingClientRect();
      const containerCenterY = containerRect.top + containerRect.height / 2;

      let closestPage = 1;
      let minDistance = Infinity;

      pageRefs.current.forEach((ref, index) => {
        if (ref) {
          const pageRect = ref.getBoundingClientRect();
          const pageCenterY = pageRect.top + pageRect.height / 2;
          const distance = Math.abs(containerCenterY - pageCenterY);

          if (distance < minDistance) {
            minDistance = distance;
            closestPage = index + 1;
          }
        }
      });

      setCurrentPage(closestPage);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [numPages]);

  // ResizeObserver to adjust the PDF width to fit the container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Measure footer height to add bottom padding to scroll area
  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return undefined;

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setFooterHeight(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
      }
    });

    resizeObserver.observe(footer);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      height='100%'
      maxWidth='90%'
      position='relative'
      borderRadius='l'
      overflow='hidden'
      boxShadow='0 8px 32px rgba(0,0,0,0.4)'
    >
      {/* Header */}
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        p='s'
        background='rgba(0,0,0,0.7)'
      >
        {title ? (
          <Typography variant='h4' color='white' ellipsis numberOfLines={1} width='100%'>
            {title}
          </Typography>
        ) : null}
        <Button onClick={onCloseDocument} variant='link/primary'>
          <Svg name='close' size='m' fill='white' />
        </Button>
      </Box>

      {/* PDF Content */}
      <Box ref={containerRef} flex={1} position='relative' overflow='auto'>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          minWidth={zoom > 1 ? `${pageWidth}px` : '100%'}
          style={{ paddingBottom: footerHeight }}
        >
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <Box p='m' textAlign='center'>
                <Loader size='xxl' />
              </Box>
            }
            error={
              <Box p='m'>
                <AlertBadge iconName='info' type='error' label={t('genericDocumentError')} />
              </Box>
            }
          >
            {pages.map(pageNumber => (
              <Box
                key={pageNumber}
                ref={el => {
                  pageRefs.current[pageNumber - 1] = el;
                }}
                borderRadius='m'
                overflow='hidden'
                mb='m'
              >
                <Page
                  pageNumber={pageNumber}
                  width={pageWidth > 0 ? pageWidth : undefined}
                  renderTextLayer
                  renderAnnotationLayer
                  loading={
                    <Box p='m' textAlign='center'>
                      <Loader size='xxl' />
                    </Box>
                  }
                />
              </Box>
            ))}
          </Document>
        </Box>
      </Box>

      {/* Footer Controls (overlay) */}
      <Box
        ref={footerRef}
        position='absolute'
        bottom={0}
        left={0}
        right={0}
        style={{ pointerEvents: 'none' }}
        zIndex={9999}
      >
        {currentPage === numPages && numPages > 0 && onFinishReading && (
          <Box
            display='flex'
            justifyContent='flex-end'
            margin='auto'
            mb='xs'
            width={pageWidth > 0 ? `${pageWidth}px` : '100%'}
            style={{ pointerEvents: 'auto' }}
          >
            <Button onClick={onFinishReading} variant='primary'>
              {t('finishReading')}
            </Button>
          </Box>
        )}
        <Box display='flex' justifyContent='center'>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            px='s'
            py='xxs'
            // borderRadius='m'
            background='rgba(0,0,0,0.7)'
            width={pageWidth > 0 ? `${pageWidth}px` : '100%'}
            maxWidth='100%'
            style={{ pointerEvents: 'auto' }}
          >
            {/* Empty space for alignment */}
            <Box display='flex' alignItems='center' flex={1} />

            {/* Page Indicator */}
            <Box display='flex' alignItems='center' flex={1} justifyContent='center'>
              <Typography color='white' variant='p2'>
                {currentPage} / {numPages || '-'}
              </Typography>
            </Box>

            {/* Zoom Controls */}
            <Box display='flex' alignItems='center' flex={1} justifyContent='flex-end'>
              <Button onClick={zoomOut} disabled={zoom <= MIN_ZOOM} variant='link/primary' mr='xxs'>
                <Svg name='zoomOut' size='xs' fill='white' />
              </Button>
              <Button onClick={resetZoom} variant='link/primary'>
                <Typography color='white' variant='p2'>
                  {Math.round(zoom * 100)}%
                </Typography>
              </Button>
              <Button onClick={zoomIn} disabled={zoom >= MAX_ZOOM} variant='link/primary' ml='xxs'>
                <Svg name='zoomIn' size='xs' fill='white' />
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
